import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { CalendarDays, MapPin, Clock, Users, BadgeDollarSign, Link2, Check } from 'lucide-react';
import { toast } from 'sonner';
import RegistrationForm from '@/components/RegistrationForm';
import { useGirlsSpots, spotsText } from '@/components/GirlsWinter';
import { GIRLS_WINTER } from '@/data/content';
import { fadeUp, stagger, viewportOnce, EASE } from '@/lib/motion';

export default function GirlsWinterPage() {
  const [copied, setCopied] = useState(false);
  const spots = useGirlsSpots();
  const g = GIRLS_WINTER;
  const pageUrl = `${window.location.origin}/girls-winter`;

  useEffect(() => {
    document.title = 'Girls Winter Soccer Training | Abu Zama Soccer Academy — Canton, Michigan';
    window.scrollTo(0, 0);
  }, []);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(pageUrl);
      setCopied(true);
      toast.success('Link copied — share it anywhere');
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error(pageUrl);
    }
  };

  return (
    <main data-testid="girls-winter-page">
      <section className="relative pt-32 pb-16 sm:pt-40 sm:pb-20 overflow-hidden" data-testid="girls-winter-hero">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(230,57,70,0.12),transparent_55%)]" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-10 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: EASE }}
            className="rounded-sm overflow-hidden gold-hairline shadow-[0_0_60px_rgba(230,57,70,0.15)]"
          >
            <img src={g.flyer} alt="Girls Winter Soccer Training — Abu Zama Soccer Academy" className="w-full object-cover" data-testid="girls-winter-page-flyer" />
          </motion.div>

          <div>
            <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}
              className="text-brand-gold text-xs sm:text-sm tracking-[0.3em] uppercase font-semibold">
              Winter Season — Girls Only — Canton, Michigan
            </motion.p>
            <h1 className="mt-4 font-display uppercase tracking-tight leading-[0.92] text-6xl sm:text-7xl text-white" data-testid="girls-winter-page-headline">
              <span className="block overflow-hidden pb-1"><motion.span className="block" initial={{ y: '110%' }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>GIRLS WINTER</motion.span></span>
              <span className="block overflow-hidden pb-1"><motion.span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-goldlight via-brand-gold to-brand-goldlight" initial={{ y: '110%' }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.3, ease: EASE }}>SOCCER TRAINING</motion.span></span>
            </h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
              className="mt-5 text-base sm:text-lg text-gray-300 leading-relaxed max-w-lg">
              {g.blurb}
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
              className="mt-7 space-y-3 text-sm sm:text-base" data-testid="girls-winter-page-details">
              <div className="flex items-center gap-3 text-gray-200"><CalendarDays size={17} className="text-brand-gold shrink-0" /><span className="font-semibold">{g.schedule}</span></div>
              <div className="flex items-center gap-3 text-gray-200"><MapPin size={17} className="text-brand-gold shrink-0" />{g.location}</div>
              <div className="flex items-center gap-3 text-gray-200"><Clock size={17} className="text-brand-gold shrink-0" />{g.period}</div>
              <div className="flex items-center gap-3 text-gray-200"><BadgeDollarSign size={17} className="text-brand-gold shrink-0" /><span><span className="font-bold text-white">{g.priceIntro}</span> — {g.paymentNote}</span></div>
              <div className="flex items-center gap-3"><Users size={17} className="text-brand-red shrink-0" /><span className="text-brand-red font-bold uppercase tracking-wide" data-testid="girls-winter-page-spots">{spotsText(spots, g.spots)}</span></div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8, ease: EASE }}
              className="mt-6 flex flex-wrap gap-2">
              {g.focus.map(f => (
                <span key={f} className="gold-hairline rounded-full px-4 py-2 text-xs sm:text-sm text-brand-goldlight font-semibold uppercase tracking-widest">{f}</span>
              ))}
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
              className="mt-8 flex flex-wrap gap-3">
              <a href="#girls-winter-register" data-testid="girls-winter-page-register-cta"
                className="bg-brand-red hover:bg-brand-redhover text-white font-display tracking-widest text-xl sm:text-2xl px-10 py-4 rounded-sm transition-colors duration-300 shadow-[0_0_40px_rgba(230,57,70,0.35)]">
                REGISTER — GIRLS WINTER
              </a>
              <button onClick={copyLink} data-testid="girls-winter-copy-link"
                className="flex items-center gap-2 border border-brand-gold/60 text-brand-gold hover:bg-brand-gold hover:text-night font-display tracking-widest text-lg px-6 py-4 rounded-sm transition-colors duration-300">
                {copied ? <Check size={18} /> : <Link2 size={18} />} {copied ? 'COPIED!' : 'COPY PAGE LINK'}
              </button>
              <a href={`https://wa.me/?text=${encodeURIComponent(`Girls Winter Soccer Training at Abu Zama Soccer Academy — ${pageUrl}`)}`} target="_blank" rel="noreferrer" data-testid="girls-winter-share-whatsapp"
                className="flex items-center gap-2 border border-[#25D366]/60 text-[#25D366] hover:bg-[#25D366] hover:text-night font-display tracking-widest text-lg px-6 py-4 rounded-sm transition-colors duration-300">
                SHARE ON WHATSAPP
              </a>
            </motion.div>
            <p className="mt-4 text-xs text-gray-500">Tip: paste this link in your Instagram bio or story — <span className="text-brand-gold">{pageUrl}</span></p>
          </div>
        </div>
      </section>

      <section className="py-16 sm:py-20 bg-night-surface border-y border-white/5" data-testid="girls-winter-page-schedule">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.h2 variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}
            className="font-display text-3xl sm:text-4xl text-white uppercase tracking-wide mb-8">
            Training Dates & <span className="text-brand-gold">Payment Due Dates</span>
          </motion.h2>
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce}
            className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {g.months.map((m, i) => (
              <motion.div key={m.month} variants={fadeUp} custom={i} className="glass-card rounded-sm p-5 hover:border-brand-gold/40 transition-[border-color] duration-300">
                <div className="font-display text-xl tracking-widest text-brand-gold uppercase">{m.month}</div>
                <div className="mt-3 text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold">Practice Dates</div>
                <div className="mt-1 text-sm text-gray-300 leading-relaxed">{m.dates}</div>
                <div className="mt-3 text-xs uppercase tracking-[0.2em] text-gray-500 font-semibold">Payment Due</div>
                <div className="mt-1 text-sm font-bold text-white">{m.paymentDue}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      <section id="girls-winter-register" className="py-20 sm:py-28 bg-night" data-testid="girls-winter-form-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce}>
            <RegistrationForm
              initialProgram="Girls Winter Training"
              formType="girls-winter"
              heading="REGISTER — GIRLS WINTER SOCCER TRAINING"
            />
          </motion.div>
        </div>
      </section>
    </main>
  );
}
